import React, { useState, useRef, useEffect } from 'react';
import { Check, X, RotateCcw } from 'lucide-react';

interface ImageCropperProps {
    imageUrl: string;
    onCropComplete: (croppedBlob: Blob) => void;
    onCancel: () => void;
}

const ImageCropper: React.FC<ImageCropperProps> = ({ imageUrl, onCropComplete, onCancel }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startPos, setStartPos] = useState({ x: 0, y: 0 });
    const [currentPos, setCurrentPos] = useState({ x: 0, y: 0 });
    const [cropRect, setCropRect] = useState<{ x: number, y: number, width: number, height: number } | null>(null);

    // Load image onto canvas
    useEffect(() => {
        const image = new Image();
        // Only set crossOrigin if not a blob URL to avoid potential issues
        if (!imageUrl.startsWith('blob:')) {
            image.crossOrigin = "anonymous";
        }

        image.onload = () => {
            if (canvasRef.current) {
                const canvas = canvasRef.current;
                const maxWidth = 600;
                const maxHeight = 400;

                let scale = Math.min(maxWidth / image.width, maxHeight / image.height);
                scale = Math.min(scale, 1); // Don't upscale

                canvas.width = image.width * scale;
                canvas.height = image.height * scale;

                const ctx = canvas.getContext('2d');
                if (ctx) {
                    ctx.clearRect(0, 0, canvas.width, canvas.height);
                    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

                    // Redraw with overlay
                    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
                    ctx.fillRect(0, 0, canvas.width, canvas.height);
                }
            }
        };

        image.onerror = (e) => {
            console.error('Failed to load image for cropping:', e);
            // Handle error state if needed
            if (onCancel) onCancel();
        };

        image.src = imageUrl;
        imageRef.current = image;

        return () => {
            // Cleanup
            image.onload = null;
            image.onerror = null;
        };
    }, [imageUrl, onCancel]);

    // Draw selection rectangle
    useEffect(() => {
        if (!canvasRef.current || !imageRef.current) return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Redraw image
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(imageRef.current, 0, 0, canvas.width, canvas.height);

        // Draw overlay
        ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        let rect = cropRect;

        // If dragging, calculate rect from startPos to currentPos
        if (isDragging) {
            const width = currentPos.x - startPos.x;
            const height = currentPos.y - startPos.y;
            rect = {
                x: width > 0 ? startPos.x : currentPos.x,
                y: height > 0 ? startPos.y : currentPos.y,
                width: Math.abs(width),
                height: Math.abs(height)
            };
        }

        if (rect) {
            // Clear selection area (make it bright)
            ctx.clearRect(rect.x, rect.y, rect.width, rect.height);
            ctx.drawImage(
                imageRef.current,
                rect.x * (imageRef.current.width / canvas.width),
                rect.y * (imageRef.current.height / canvas.height),
                rect.width * (imageRef.current.width / canvas.width),
                rect.height * (imageRef.current.height / canvas.height),
                rect.x,
                rect.y,
                rect.width,
                rect.height
            );

            // Draw border
            ctx.strokeStyle = '#fff';
            ctx.lineWidth = 2;
            ctx.strokeRect(rect.x, rect.y, rect.width, rect.height);
        }

    }, [isDragging, startPos, currentPos, cropRect]);

    const getEventPos = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };

        const rect = canvas.getBoundingClientRect();
        let clientX: number, clientY: number;

        if ('touches' in e) {
            // Touch event
            if (e.touches && e.touches.length > 0) {
                clientX = e.touches[0].clientX;
                clientY = e.touches[0].clientY;
            } else if (e.changedTouches && e.changedTouches.length > 0) {
                clientX = e.changedTouches[0].clientX;
                clientY = e.changedTouches[0].clientY;
            } else {
                return { x: 0, y: 0 };
            }
        } else {
            // Mouse event
            clientX = e.clientX;
            clientY = e.clientY;
        }

        // Calculate position relative to canvas
        const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
        const y = Math.max(0, Math.min(clientY - rect.top, rect.height));

        return { x, y };
    };

    const handleStart = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        e.preventDefault();
        const pos = getEventPos(e);
        setStartPos(pos);
        setCurrentPos(pos);
        setIsDragging(true);
        setCropRect(null); // Reset previous crop
    };

    const handleMove = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        if (!isDragging) return;
        e.preventDefault();
        const pos = getEventPos(e);
        setCurrentPos(pos);
    };

    const handleEnd = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
        if (!isDragging) return;
        e.preventDefault();
        setIsDragging(false);

        // Finalize rect
        const width = currentPos.x - startPos.x;
        const height = currentPos.y - startPos.y;

        // Ignore very small selections
        if (Math.abs(width) < 10 || Math.abs(height) < 10) {
            setCropRect(null);
            return;
        }

        setCropRect({
            x: width > 0 ? startPos.x : currentPos.x,
            y: height > 0 ? startPos.y : currentPos.y,
            width: Math.abs(width),
            height: Math.abs(height)
        });
    };

    const handleConfirm = () => {
        console.log('✂️ [ImageCropper] Confirm clicked', { cropRect, hasCanvas: !!canvasRef.current, hasImage: !!imageRef.current });

        if (!cropRect || !canvasRef.current || !imageRef.current) {
            // If no crop, just return original
            console.log('✂️ [ImageCropper] No crop rect, returning original');
            fetch(imageUrl)
                .then(r => r.blob())
                .then(blob => {
                    console.log('✂️ [ImageCropper] Original blob fetched', blob.size);
                    onCropComplete(blob);
                })
                .catch(err => console.error('❌ [ImageCropper] Failed to fetch original:', err));
            return;
        }

        const canvas = canvasRef.current;
        const scaleX = imageRef.current.width / canvas.width;
        const scaleY = imageRef.current.height / canvas.height;

        const finalCanvas = document.createElement('canvas');
        finalCanvas.width = cropRect.width * scaleX;
        finalCanvas.height = cropRect.height * scaleY;

        const ctx = finalCanvas.getContext('2d');
        if (ctx) {
            ctx.drawImage(
                imageRef.current,
                cropRect.x * scaleX,
                cropRect.y * scaleY,
                cropRect.width * scaleX,
                cropRect.height * scaleY,
                0,
                0,
                finalCanvas.width,
                finalCanvas.height
            );

            finalCanvas.toBlob((blob) => {
                if (blob) {
                    console.log('✂️ [ImageCropper] Crop blob created', blob.size);
                    onCropComplete(blob);
                } else {
                    console.error('❌ [ImageCropper] Failed to create blob');
                }
            }, 'image/jpeg', 0.9);
        } else {
            console.error('❌ [ImageCropper] Failed to get 2d context');
        }
    };

    return (
        <div className="space-y-4 bg-white rounded-xl p-4 border border-gray-200">
            <div className="text-center mb-4">
                <h4 className="font-semibold text-gray-800 mb-2">Cắt ảnh</h4>
                <p className="text-sm text-gray-500">Kéo thả để chọn vùng cần chấm điểm</p>
            </div>

            <div className="relative w-full flex justify-center bg-gray-900 rounded-lg overflow-hidden">
                <canvas
                    ref={canvasRef}
                    onMouseDown={handleStart}
                    onMouseMove={handleMove}
                    onMouseUp={handleEnd}
                    onMouseLeave={handleEnd}
                    onTouchStart={handleStart}
                    onTouchMove={handleMove}
                    onTouchEnd={handleEnd}
                    className="max-w-full cursor-crosshair touch-none"
                    style={{ touchAction: 'none' }}
                />
            </div>

            <div className="flex gap-3 justify-center flex-wrap">
                <button
                    onClick={onCancel}
                    className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 flex items-center gap-2 font-medium transition-colors"
                >
                    <X className="w-4 h-4" />
                    Hủy
                </button>
                <button
                    onClick={() => setCropRect(null)}
                    className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 flex items-center gap-2 font-medium transition-colors"
                >
                    <RotateCcw className="w-4 h-4" />
                    Chọn lại
                </button>
                <button
                    onClick={handleConfirm}
                    className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-500 flex items-center gap-2 font-medium transition-colors shadow-sm"
                >
                    <Check className="w-4 h-4" />
                    Xong
                </button>
            </div>
        </div>
    );
};

export default ImageCropper;
